import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import SortIcon from './SortIcon'

/**
 * Table header cell component with optional sorting
 *
 * @param {Object} props - Component props
 * @param {string} props.sortKey - Key used for sorting (enables sorting when provided)
 * @param {string} props.currentSort - Currently sorted column key
 * @param {string} props.sortDirection - Current sort direction ('asc' or 'desc')
 * @param {Function} props.onSort - Callback when sort is triggered
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.style - Inline styles
 */
const TableHeader = ({
  sortKey = null,
  currentSort = null,
  sortDirection = 'desc',
  onSort = null,
  disabled = false,
  className = '',
  style = null,
  children,
  ...props
}) => {
  const isSortable = Boolean(sortKey && onSort)
  const isActive = currentSort === sortKey

  const handleClick = useCallback(() => {
    if (isSortable && !disabled)
      onSort(sortKey)
  }, [isSortable, disabled, onSort, sortKey])

  const classes = [
    'tw:whitespace-nowrap',
    isSortable && (disabled ? 'tw:cursor-not-allowed' : 'c-pointer'),
    className
  ].filter(Boolean).join(' ')

  const iconClasses = ['tw:inline-block', disabled && 'tw:opacity-50'].filter(Boolean).join(' ')

  return (
    <th
      aria-disabled={isSortable && disabled ? 'true' : null}
      className={classes}
      style={style}
      onClick={isSortable ? handleClick : null}
      {...props}
    >
      {children}
      {isSortable ? (
        <span className={iconClasses}>
          <SortIcon active={isActive} direction={isActive ? sortDirection : 'desc'} />
        </span>
      ) : null}
    </th>
  )
}

TableHeader.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  currentSort: PropTypes.string,
  disabled: PropTypes.bool,
  onSort: PropTypes.func,
  sortDirection: PropTypes.oneOf(['asc', 'desc']),
  sortKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  style: PropTypes.shape({})
}

TableHeader.defaultProps = {
  className: '',
  currentSort: null,
  disabled: false,
  onSort: null,
  sortDirection: 'desc',
  sortKey: null,
  style: null
}

export default TableHeader
